import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { titleCase } from 'app/utils/titleCase'

const BASE_TITLE = `Trackable`

/**
 * Sets document.title based on the active sidebar item name.
 * Falls back to deriving a title from the URL pathname when no
 * sidebar item is active (e.g. Calls page which is not in the sidebar).
 * Preserves any notification count prefix like "(3) ".
 *
 * @param pageName - The name of the current page (from sidebar active item)
 */
export function useDocumentTitle(pageName: string | undefined): void {
  const location = useLocation()

  useEffect(() => {
    const notificationPrefix = document.title.match(/^\(\d+\)\s/)?.[0] || ``
    const title = pageName || titleFromPathname(location.pathname)
    if (title) {
      document.title = `${notificationPrefix}${BASE_TITLE} - ${title}`
    } else {
      document.title = `${notificationPrefix}${BASE_TITLE}`
    }
  }, [pageName, location.pathname])
}

/**
 * Fallback: derive a page title from the URL when no sidebar item is active.
 * Uses the first non-dynamic pathname segment and applies titleCase.
 */
function titleFromPathname(pathname: string): string | undefined {
  const segments = pathname.split(`/`).filter(Boolean)
  if (segments.length === 0) return undefined

  const segment = segments[0]
  if (looksLikeDynamicParam(segment)) return undefined
  return titleCase(segment.replace(/-/g, ` `))
}

function looksLikeDynamicParam(segment: string): boolean {
  return (
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(segment) ||
    /^\d+$/.test(segment) ||
    /^[0-9a-f]{20,}$/i.test(segment)
  )
}
